import {
  banEmployee,
  createEmployee,
  createReplenishment,
  deleteSupply,
  enableEmployee,
  fetchDashboard,
  fetchEmployees,
  fetchReplenishments,
  fetchRequests,
  fetchSupplies,
  fetchWarnings,
  processReplenishment,
  rejectReplenishment,
  rejectRequest,
  reviewRequest,
  saveSupply,
  updateSupply
} from '../api/system'

function normalizeSupplyRecord(record) {
  const warningNum = Number(record.warningNum ?? record.warning_num ?? 0)
  const stock = Number(record.stock ?? 0)

  return {
    ...record,
    warningNum,
    warning: warningNum > stock
  }
}

function normalizeEmployeeRecord(record) {
  const role = Number(record.role ?? 3)
  const status = Number(record.status ?? 1)

  return {
    ...record,
    role,
    roleLabel: role === 2 ? '企业管理员' : role === 3 ? '普通员工' : '未知角色',
    createTime: record.createTime || record.create_time || '',
    status,
    statusLabel: status === 1 ? '正常' : '封禁'
  }
}

function normalizePagedData(data) {
  return {
    ...data,
    content: data?.content || [],
    totalElements: data?.totalElements ?? 0
  }
}

function normalizeDashboardData(data) {
  const source = data || {}
  const metrics = source.metrics || {}

  return {
    metrics: {
      totalRequests: Number(metrics.totalRequests ?? 0),
      approvedCount: Number(metrics.approvedCount ?? 0),
      totalCost: Number(metrics.totalCost ?? 0)
    },
    trend: Array.isArray(source.trend)
      ? source.trend.map((item) => ({
          label: item?.label || '',
          value: Number(item?.value ?? 0)
        }))
      : [],
    topSupplies: Array.isArray(source.topSupplies)
      ? source.topSupplies.map((item) => ({
          name: item?.name || '',
          value: Number(item?.value ?? 0)
        }))
      : [],
    costDistribution: Array.isArray(source.costDistribution)
      ? source.costDistribution.map((item) => ({
          name: item?.name || '',
          value: Number(item?.value ?? 0)
        }))
      : []
  }
}

function normalizeRequestRecord(record) {
  const applyStatus = Number(record.applyStatus ?? 0)
  let supplies = record.suppliesJson || record.supplies_json || {}
  let user = record.userJson || record.user_json || {}

  if (typeof supplies === 'string') {
    try {
      supplies = JSON.parse(supplies)
    } catch (error) {
      supplies = {}
    }
  }

  if (typeof user === 'string') {
    try {
      user = JSON.parse(user)
    } catch (error) {
      user = {}
    }
  }

  return {
    ...record,
    applyStatus,
    createTime: record.createTime || record.create_time || '',
    rejectReason: record.rejectReason || record.reject_reason || '',
    suppliesName: supplies?.name || record.suppliesName || record.supplies_name || '',
    userUsername: user?.username || record.userUsername || record.username || ''
  }
}

function normalizeReplenishmentRecord(record) {
  const applyStatus = Number(record.applyStatus ?? record.apply_status ?? 0)
  let supplies = record.suppliesJson || record.supplies_json || {}

  if (typeof supplies === 'string') {
    try {
      supplies = JSON.parse(supplies)
    } catch (error) {
      supplies = {}
    }
  }

  return {
    ...record,
    applyStatus,
    createTime: record.createTime || record.create_time || '',
    applyTime: record.applyTime || record.apply_time || '',
    rejectReason: record.rejectReason || record.reject_reason || '',
    suppliesName: supplies?.name || record.suppliesName || record.supplies_name || '',
    suppliesSpec: supplies?.spec || record.suppliesSpec || record.supplies_spec || '',
    suppliesStock: supplies?.stock ?? record.suppliesStock ?? record.stock ?? 0,
    suppliesPrice: supplies?.price ?? record.suppliesPrice ?? record.price ?? 0,
    suppliesLocation: supplies?.location || record.suppliesLocation || record.location || '',
    statusLabel: applyStatus === 0 ? '待补货' : applyStatus === 1 ? '已入库' : '已拒绝'
  }
}

function fetchAdminDashboard() {
  return fetchDashboard().then((response) => ({
    ...response,
    data: normalizeDashboardData(response.data)
  }))
}

async function fetchAdminSupplies(filters) {
  const response = await fetchSupplies(filters.page, filters.pageSize, {
    name: filters.search
  })

  return {
    ...response,
    data: {
      ...response.data,
      list: (response.data?.list || []).map(normalizeSupplyRecord),
      content: (response.data?.content || []).map(normalizeSupplyRecord)
    }
  }
}

function saveAdminSupply(form) {
  return saveSupply({
    ...form
  })
}

function updateAdminSupply(form) {
  return updateSupply({
    ...form
  })
}

function deleteAdminSupply(supplyId) {
  return deleteSupply({
    id: supplyId
  })
}

async function fetchAdminRequests(filters) {
  const response = await fetchRequests(filters.page, filters.pageSize, {
    applyStatus: filters.applyStatus
  })

  return {
    ...response,
    data: {
      ...normalizePagedData(response.data),
      content: (response.data?.content || []).map(normalizeRequestRecord)
    }
  }
}

function approveAdminRequest(requestId) {
  return reviewRequest({
    id: requestId
  })
}

function rejectAdminRequest(requestId, rejectReason) {
  return rejectRequest({
    requestId,
    rejectReason
  })
}

async function fetchAdminWarnings() {
  const response = await fetchWarnings()

  return {
    ...response,
    data: (response.data || []).map(normalizeSupplyRecord)
  }
}

async function fetchAdminReplenishments(filters) {
  const response = await fetchReplenishments(filters.page, filters.pageSize, {
    applyStatus: filters.applyStatus
  })

  return {
    ...response,
    data: {
      ...normalizePagedData(response.data),
      content: (response.data?.content || response.data?.list || []).map(normalizeReplenishmentRecord)
    }
  }
}

function createAdminReplenishment(suppliesId, quantity) {
  return createReplenishment({
    supplies_id: suppliesId,
    quantity
  })
}

function approveAdminReplenishment(replenishmentId, quantity) {
  return processReplenishment({
    id: replenishmentId,
    quantity
  })
}

function rejectAdminReplenishment(replenishmentId, rejectReason) {
  return rejectReplenishment({
    id: replenishmentId,
    rejectReason
  })
}

async function fetchAdminEmployees(filters) {
  const response = await fetchEmployees(filters.page, filters.pageSize, {
    username: filters.search
  })

  return {
    ...response,
    data: {
      ...response.data,
      list: (response.data?.list || response.data?.content || []).map(normalizeEmployeeRecord),
      content: (response.data?.content || response.data?.list || []).map(normalizeEmployeeRecord),
      total: response.data?.total ?? response.data?.totalElements ?? 0,
      totalElements: response.data?.totalElements ?? response.data?.total ?? 0
    }
  }
}

function createAdminEmployee(form) {
  return createEmployee({
    ...form
  })
}

function updateAdminEmployeeStatus(id, status) {
  //这里是nextstatus，0封禁，1启用
  if (Number(status) === 0) {
    return banEmployee(id)
  }

  return enableEmployee(id)
}

export {
  approveAdminReplenishment,
  approveAdminRequest,
  createAdminEmployee,
  createAdminReplenishment,
  deleteAdminSupply,
  fetchAdminDashboard,
  fetchAdminEmployees,
  fetchAdminReplenishments,
  fetchAdminRequests,
  fetchAdminSupplies,
  fetchAdminWarnings,
  rejectAdminReplenishment,
  rejectAdminRequest,
  saveAdminSupply,
  updateAdminEmployeeStatus,
  updateAdminSupply
}
